import type { ReactNode } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';

import { ErrorContext } from './ErrorContext';
const EMIT_ERROR_MESSAGES = [
  'Abort fetching component',
  'Aborted fetching component',
];

function isErrorFilteredByMessage(value: string | Error | undefined): boolean {
  if (!value) return false;
  const message = typeof value === 'string' ? value : value.message;
  return message
    ? EMIT_ERROR_MESSAGES.some((msg) => message.includes(msg))
    : false;
}

export const ErrorProvider = ({ children }: { children: ReactNode }) => {
  const [error, setError] = useState<{
    message: string;
    source: string;
  } | null>(null);

  const value = useMemo(() => ({ error, setError }), [error]);

  useEffect(() => {
    const originalOnError = window.onerror;
    const originalOnUnhandledRejection = window.onunhandledrejection;
    const originalConsoleError = console.error;

    console.error = function (...args: (string | Error)[]): void {
      const isFiltered = args.some((arg) => isErrorFilteredByMessage(arg));

      if (isFiltered) return;
      originalConsoleError.apply(console, args);
    };

    return () => {
      window.onerror = originalOnError;
      window.onunhandledrejection = originalOnUnhandledRejection;
      console.error = originalConsoleError;
    };
  }, []);

  return (
    <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};
